import * as React from "react";
import { observer } from "mobx-react-lite";
import { WorkloadDraftMobx } from "../../../mst/stores/workload.draft";
import { ScheduleEntryDraftMobx, ContainerOverrideDraftMobx } from "../../../mst/stores/workload.draft.job";
import { NGLabel } from "../../../newcomponents/text/label";
import { NGInput } from "../../../newcomponents/input/input";
import { NGSelect } from "../../../newcomponents/select/ngselect";
import { NGInputAdapter } from "../../../newcomponents/input/inputAdapter";
import { NGButton } from "../../../newcomponents/button/Button";
import { InfoTooltip } from "../../../components/InfoTooltip";
import cronstrue from "cronstrue";
import { NGTextArea } from "../../../newcomponents/textarea";
import { NGInputListMst } from "../../../newcomponents/inputList/inputListMst";
import { memoryUnitOptions } from "../../../mobxDataModels/memoryModel";
import { cpuUnitOptions } from "../../../mobxDataModels/cpuModel";

interface ContainerOverrideRowProps {
  override: ContainerOverrideDraftMobx;
  onRemove: () => void;
  containerNameOptions: { label: string; value: string; disabled?: boolean }[];
}
const ContainerOverrideRowRaw: React.FC<ContainerOverrideRowProps> = ({ override, onRemove, containerNameOptions }) => (
  <div className="mb-4 pl-4 border-l-2 border-cpln-gray-light" style={{ maxWidth: 800 }}>
    <div className="flex items-end gap-2 mb-2">
      <div>
        <NGLabel size={2}>Container Name</NGLabel>
        <NGSelect
          style={{ width: 200 }}
          value={override.name}
          onChange={(v) => override.setField("name", v)}
          options={containerNameOptions}
        />
      </div>
    </div>
    <div className="flex items-end gap-2 mb-2 flex-grow">
      <div className="flex-grow">
        <NGLabel size={2}>Memory</NGLabel>
        <div className="flex gap-1">
          <NGInput
            className="flex-grow"
            value={override.memoryValue}
            onChange={(e) => override.setField("memoryValue", e.target.value)}
            placeholder={override.memoryUnit === "Other" ? "128Mi" : "128"}
          />
          <NGSelect
            style={{ width: 130 }}
            value={override.memoryUnit}
            onChange={(v) => override.setField("memoryUnit", v)}
            options={memoryUnitOptions}
          />
        </div>
      </div>
      <div className="flex-grow">
        <NGLabel size={2}>CPU</NGLabel>
        <div className="flex gap-1">
          <NGInput
            className="flex-grow"
            value={override.cpuValue}
            onChange={(e) => override.setField("cpuValue", e.target.value)}
            placeholder={override.cpuUnit === "Cores" ? "0.5" : "500"}
          />
          <NGSelect
            style={{ width: 130 }}
            value={override.cpuUnit}
            onChange={(v) => override.setField("cpuUnit", v)}
            options={cpuUnitOptions}
          />
        </div>
      </div>
    </div>
    <div className="mb-2">
      <NGLabel size={2}>Image</NGLabel>
      <NGInput
        value={override.image}
        onChange={(e) => override.setField("image", e.target.value)}
        placeholder="image:tag"
      />
    </div>
    <div className="mb-2">
      <NGLabel size={2}>Command</NGLabel>
      <NGTextArea
        value={override.command}
        onChange={(e) => override.setField("command", e.target.value)}
        placeholder="command"
      />
    </div>
    <NGInputListMst
      label="Args"
      data={override.args}
      draggable
      firstInput={(item) => (
        <NGTextArea
          value={item.firstValue}
          placeholder="Multiline Input"
          onChange={(e) => item.setFirstValue(e.target.value)}
          invalid={!item.firstValue}
        />
      )}
      invalid={override.args.items.some((i) => !i.firstValue)}
      error={override.args.items.some((i) => !i.firstValue) ? "Items cannot be empty" : ""}
    />
    <div className="flex justify-end mt-2">
      <NGButton variant="danger" outlined size="small" onClick={onRemove}>
        Remove Container Override
      </NGButton>
    </div>
  </div>
);
const ContainerOverrideRow = observer(ContainerOverrideRowRaw);

interface ScheduleEntryRowProps {
  entry: ScheduleEntryDraftMobx;
  onRemove: () => void;
  showRemove: boolean;
  containerNames: string[];
}
const ScheduleEntryRowRaw: React.FC<ScheduleEntryRowProps> = ({ entry, onRemove, showRemove, containerNames }) => {
  const [showOverrides, setShowOverrides] = React.useState(false);

  const usedNames = entry.containerOverrides.map((co) => co.name);
  const allUsed = containerNames.length > 0 && entry.containerOverrides.length >= containerNames.length;

  function handleAddOverride() {
    const unusedBefore = containerNames.filter((n) => !usedNames.includes(n));
    entry.addOverride();
    if (unusedBefore.length === 1) {
      const newOverride = entry.containerOverrides[entry.containerOverrides.length - 1];
      newOverride.setField("name", unusedBefore[0]);
    }
  }

  return (
    <div className="mb-4 p-3 border border-cpln-gray-light rounded" style={{ width: "fit-content" }}>
      <div className="flex gap-3 items-end mb-2">
        <div>
          <NGLabel size={2}>Name</NGLabel>
          <NGInput
            style={{ width: 180 }}
            value={entry.name}
            onChange={(e) => entry.setName(e.target.value)}
            placeholder="schedule-name"
          />
        </div>
        <div>
          <NGLabel size={2}>Schedule (UTC)</NGLabel>
          <NGInput
            style={{ width: 220 }}
            value={entry.schedule}
            onChange={(e) => entry.setSchedule(e.target.value)}
            placeholder="* * * * *"
          />
        </div>
      </div>
      {entry.schedule.length > 0 && (() => {
        try {
          const text = cronstrue.toString(entry.schedule, { throwExceptionOnParseError: true, use24HourTimeFormat: true });
          return <div className="text-sm text-cpln-gray mb-2">{text}</div>;
        } catch {
          return null;
        }
      })()}
      <div className="mt-1">
        <button
          type="button"
          className="text-sm text-cpln-blue hover:underline"
          onClick={() => setShowOverrides((v) => !v)}
        >
          {showOverrides ? "Hide" : "Show"} Container Overrides ({entry.containerOverrides.length})
        </button>
        {showOverrides && (
          <div className="mt-2" style={{ width: "fit-content" }}>
            {entry.containerOverrides.map((co) => {
              const options = containerNames.map((n) => ({
                label: n,
                value: n,
                disabled: n !== co.name && usedNames.includes(n),
              }));
              return (
                <ContainerOverrideRow
                  key={co.id}
                  override={co}
                  onRemove={() => entry.removeOverride(co.id)}
                  containerNameOptions={options}
                />
              );
            })}
            <NGButton variant="action" outlined size="small" disabled={allUsed} onClick={handleAddOverride}>
              Add Override
            </NGButton>
          </div>
        )}
      </div>
      {showRemove && (
        <div className="flex justify-end mt-2">
          <NGButton variant="danger" outlined size="small" onClick={onRemove}>
            Remove Schedule
          </NGButton>
        </div>
      )}
    </div>
  );
};
const ScheduleEntryRow = observer(ScheduleEntryRowRaw);

interface Props {
  workloadDraft: WorkloadDraftMobx;
}
const WorkloadCreateJobRaw: React.FC<Props> = ({ workloadDraft }) => {
  const job = workloadDraft.job;

  return (
    <>
      <div className="mb-4">
        <NGLabel>Schedule Mode</NGLabel>
        <div className="flex gap-2 mb-4">
          <NGButton
            variant={job.scheduleMode === "single" ? "primary" : "secondary"}
            outlined={job.scheduleMode !== "single"}
            onClick={() => job.setScheduleMode("single")}
          >
            Single
          </NGButton>
          <NGButton
            variant={job.scheduleMode === "multiple" ? "primary" : "secondary"}
            outlined={job.scheduleMode !== "multiple"}
            onClick={() => job.setScheduleMode("multiple")}
          >
            Multiple
          </NGButton>
        </div>

        {job.scheduleMode === "single" ? (
          <div>
            <NGLabel>Schedule (UTC)</NGLabel>
            <NGInput
              className="mb-2"
              style={{ width: 450 }}
              onChange={(e) => {
                job.schedule.setValue(e.target.value);
              }}
              value={job.schedule.value}
            />
            {job.schedule.value.length > 0 && job.schedule.isValid ? (
              <div>
                {cronstrue.toString(job.schedule.value, {
                  throwExceptionOnParseError: false,
                  use24HourTimeFormat: true,
                })}
              </div>
            ) : null}
          </div>
        ) : (
          <div>
            <NGLabel>Schedules</NGLabel>
            {job.schedules.map((entry) => (
              <ScheduleEntryRow
                key={entry.id}
                entry={entry}
                onRemove={() => job.removeScheduleEntry(entry.id)}
                showRemove={job.schedules.length > 1}
                containerNames={workloadDraft.allContainers.map((c) => c.name.value)}
              />
            ))}
            <NGButton variant="action" outlined onClick={() => job.addScheduleEntry()}>
              Add Schedule
            </NGButton>
          </div>
        )}
      </div>
      <NGLabel>{job.concurrencyPolicy.label}</NGLabel>
      <div className="mb-4 relative flex items-center">
        <NGSelect
          style={{ width: 450, zIndex: 3 }}
          onChange={job.concurrencyPolicy.setValue}
          options={job.concurrencyPolicy.options}
          value={job.concurrencyPolicy.value as any}
        />
        <InfoTooltip
          title={`Either 'Forbid', 'Replace', or 'Allow'. This determines what Control Plane will do when the schedule requires a job to start, while a prior instance of the job is still running.`}
        />
      </div>
      <NGInputAdapter
        data={job.historyLimit}
        className="mb-4"
        style={{ width: 450 }}
        infoContents={[
          `The maximum number of completed job instances to display. This should be an integer between 1 and 10.`,
        ]}
      />
      <NGLabel>{job.restartPolicy.label}</NGLabel>
      <div className="mb-4 relative flex items-center">
        <NGSelect
          className="relative"
          style={{ width: 450, zIndex: 3 }}
          onChange={job.restartPolicy.setValue}
          options={job.restartPolicy.options}
          value={job.restartPolicy.value as any}
        />
        <InfoTooltip
          title={`Either 'OnFailure' or 'Never'. This determines what Control Plane will do when a job instance fails.`}
        />
      </div>
      <NGInputAdapter
        data={job.activeDeadlineSeconds}
        className="mb-4"
        style={{ width: 450 }}
        infoContents={[
          `The maximum number of seconds Control Plane will wait for the job to complete. If a job does not succeed or fail in the allotted time, Control Plane will stop the job, moving it into the Removed status.`,
        ]}
      />
    </>
  );
};

export const WorkloadCreateJob = observer(WorkloadCreateJobRaw);
